/**
 * Animations GSAP (entrée + scroll) — accueil & pages d'offre.
 *
 * Dépend de GSAP + ScrollTrigger + SplitText (chargés en amont, voir
 * starter_enqueue_scripts() dans functions.php). Fichier isolé de main.js pour
 * garder la logique d'animation à part.
 *
 * Principe de sûreté : les états initiaux « cachés » sont posés UNIQUEMENT en JS
 * (gsap.set / gsap.from). Sans JS ou en prefers-reduced-motion, le contenu reste
 * visible.
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    if (typeof window.gsap === 'undefined') { return; }

    var gsap = window.gsap;
    var hasScrollTrigger = typeof window.ScrollTrigger !== 'undefined';
    var hasSplitText = typeof window.SplitText !== 'undefined';
    if (hasScrollTrigger) { gsap.registerPlugin(window.ScrollTrigger); }
    if (hasSplitText) { gsap.registerPlugin(window.SplitText); }

    var mm = gsap.matchMedia();

    /**
     * Résolue dès que quelque chose est peint derrière le hero — ou, au pire, après un
     * délai de sûreté. Pour une vidéo, `readyState >= 2` (HAVE_CURRENT_DATA) signifie
     * que la première frame est décodée ; et si un `poster` est défini, il tient le rôle
     * d'image d'attente : il suffit à lui seul pour lancer l'entrée du texte.
     */
    function heroMediaReady(hero) {
      var media = hero.querySelector('.hero__video, .hero__image');
      if (!media) { return Promise.resolve(); }

      var isVideo = 'VIDEO' === media.tagName;

      // Déjà peint (cache navigateur, ou frame déjà décodée).
      if (isVideo ? media.readyState >= 2 : (media.complete && media.naturalWidth)) {
        return Promise.resolve();
      }

      var painted = [ new Promise(function (resolve) {
        media.addEventListener(isVideo ? 'loadeddata' : 'load', resolve, { once: true });
        // Média cassé / codec non supporté : ne pas attendre indéfiniment.
        media.addEventListener('error', resolve, { once: true });
      }) ];

      // Avec un poster, le fond est habillé dès que cette image est chargée : inutile
      // d'attendre le buffer de la vidéo pour révéler le titre.
      var poster = isVideo ? media.getAttribute('poster') : '';
      if (poster) {
        painted.push(new Promise(function (resolve) {
          var img = new Image();
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
          img.src = poster;
          if (img.complete) { resolve(); }
        }));
      }

      // Garde-fou : un média lourd ne doit jamais laisser le texte du hero invisible.
      painted.push(new Promise(function (resolve) { setTimeout(resolve, 2500); }));

      return Promise.race(painted);
    }

    mm.add('(prefers-reduced-motion: no-preference)', function () {

      // Restaurations à rejouer si le contexte est annulé (bascule OS vers « réduire les
      // animations ») : GSAP défait seul ses propres props, mais pas un textContent réécrit.
      var restores = [];

      // A · Révélation de texte ligne par ligne (SplitText) sur tout [data-animate].
      // `mask:'lines'` pose une découpe overflow:hidden par ligne : chaque ligne
      // monte depuis derrière le masque, puis on revert() pour rendre le texte au DOM.
      // Le titre du hero est exclu : il est joué dans la séquence B, qui attend le média.
      if (hasSplitText && hasScrollTrigger) {
        var animateEls = gsap.utils.toArray('[data-animate]:not(.hero__title)');
        if (animateEls.length) {
          // On attend les polices : le découpage en lignes en dépend.
          document.fonts.ready.then(function () {
            animateEls.forEach(function (el) {
              var split = window.SplitText.create(el, {
                type: 'lines',
                mask: 'lines',
                linesClass: 'anim-line'
              });
              gsap.from(split.lines, {
                yPercent: 110,
                duration: 0.7,
                ease: 'power3.out',
                stagger: 0.15,
                scrollTrigger: {
                  trigger: el,
                  start: 'top 85%',
                  once: true
                },
                // Reveal joué une seule fois : on rend le texte au DOM une fois
                // terminé (retire le masque → plus aucun rognage des jambages).
                onComplete: function () { split.revert(); }
              });
            });
          });
        }
      }

      // B · Hero — séquence titre puis sous-titre, gatée sur le média.
      // Sans ce gate, le texte finissait son entrée sur le fond nu (#1a1a1a) et la
      // vidéo/l'image surgissait après. Le média lui-même n'est pas animé.
      var hero = document.querySelector('.hero');
      if (hero) {
        var heroTitle    = hero.querySelector('.hero__title');
        var heroSubtitle = hero.querySelector('.hero__subtitle');
        var heroEls      = [heroTitle, heroSubtitle].filter(Boolean);

        if (heroEls.length) {
          // Masquage immédiat : la révélation étant retardée, sans ça le texte
          // s'afficherait en clair puis disparaîtrait pour se re-révéler.
          gsap.set(heroEls, { autoAlpha: 0 });

          Promise.all([document.fonts.ready, heroMediaReady(hero)]).then(function () {
            var heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

            if (heroTitle && hasSplitText) {
              var heroSplit = window.SplitText.create(heroTitle, {
                type: 'lines',
                mask: 'lines',
                linesClass: 'anim-line'
              });
              // Les lignes sont déjà cachées derrière le masque : on peut rendre le
              // titre visible à l'instant exact où elles commencent à monter.
              heroTl.set(heroTitle, { autoAlpha: 1 });
              heroTl.from(heroSplit.lines, {
                yPercent: 110,
                duration: 0.7,
                stagger: 0.15,
                // Reveal joué une seule fois : on rend le texte au DOM (retire le masque).
                onComplete: function () { heroSplit.revert(); }
              });
            } else if (heroTitle) {
              heroTl.fromTo(heroTitle,
                { autoAlpha: 0, y: 24 },
                { autoAlpha: 1, y: 0, duration: 0.6 });
            }

            // Sous-titre : simple fondu (contient du HTML, pas de découpe en lignes).
            if (heroSubtitle) {
              heroTl.fromTo(heroSubtitle,
                { autoAlpha: 0, y: 28 },
                { autoAlpha: 1, y: 0, duration: 0.7 },
                heroTitle ? '-=0.3' : 0);
            }
          })['catch'](function () {
            // Filet de sécurité : le texte est masqué en amont, il ne doit jamais
            // rester invisible si la construction du timeline échoue.
            gsap.set(heroEls, { autoAlpha: 1 });
          });
        }
      }

      // Animations au scroll (nécessitent ScrollTrigger)
      if (hasScrollTrigger) {

        // D · Grille d'offres — révélation en cascade
        var cards = gsap.utils.toArray('.job-grid .job-card');
        if (cards.length) {
          gsap.set(cards, { opacity: 0, y: 48 });
          window.ScrollTrigger.batch(cards, {
            start: 'top 88%',
            onEnter: function (batch) {
              gsap.to(batch, {
                opacity: 1,
                y: 0,
                duration: 0.7,
                ease: 'power3.out',
                stagger: 0.1,
                overwrite: true,
                // On retire le transform pour ne pas casser le :hover CSS de la carte.
                onComplete: function () { gsap.set(batch, { clearProps: 'transform' }); }
              });
            }
          });
        }

        // F · Frise chronologique — révélation des dates en cascade.
        // On anime `.history__card` (l'enveloppe du contenu) et SURTOUT PAS
        // `.history__item` : ce dernier porte le point de l'axe en ::before, et un
        // pseudo-élément hérite forcément de l'opacity/du transform de son parent —
        // les points se décolleraient de la ligne pendant toute l'animation.
        // Les items sont positionnés via `left` (pas `transform`) : animer
        // opacity/y ne casse ni le vertical alterné ni l'horizontal (flex).
        gsap.utils.toArray('.history__timeline').forEach(function (timeline) {
          var cards = timeline.querySelectorAll('.history__card');
          if (!cards.length) { return; }
          var isHorizontal = timeline.closest('.history--horizontal') &&
            window.matchMedia('(min-width: 769px)').matches;
          gsap.from(cards, {
            opacity: 0,
            // Horizontal : les dates montent ; vertical : elles glissent depuis le bas.
            y: isHorizontal ? 40 : 56,
            duration: 0.6,
            ease: 'power3.out',
            stagger: 0.18, // délai entre chaque date clé
            scrollTrigger: {
              trigger: timeline,
              start: 'top 80%',
              once: true
            }
          });
        });

        // E · Chiffres-clés — effet machine à sous.
        // Chaque chiffre de la valeur saisie devient un rouleau (0→9 répété) qui défile
        // puis s'arrête sur le bon chiffre ; les autres caractères (+, %, espaces,
        // virgule…) restent fixes. Les rouleaux s'arrêtent de gauche à droite : les
        // unités tournent le plus longtemps. Le texte d'origine est remis dans le DOM
        // à la fin : la valeur affichée est exactement la saisie du Customizer.
        // (Pas en mode bento : ce sont les tuiles qui s'animent, voir bloc G.)
        gsap.utils.toArray('.key-figures:not(.key-figures--bento) .key-figures__value').forEach(function (el) {
          var raw = el.textContent.trim();
          if (!/\d/.test(raw)) { return; }

          // Rendu visuel (masqué aux lecteurs d'écran) + valeur lisible à part.
          var visual = document.createElement('span');
          visual.className = 'kf-visual';
          visual.setAttribute('aria-hidden', 'true');
          var reels = [];

          raw.split('').forEach(function (ch) {
            if (!/\d/.test(ch)) {
              visual.appendChild(document.createTextNode(ch));
              return;
            }
            var reel  = document.createElement('span');
            var strip = document.createElement('span');
            reel.className  = 'kf-reel';
            strip.className = 'kf-reel__strip';
            reel.appendChild(strip);
            visual.appendChild(reel);
            reels.push({ strip: strip, target: +ch });
          });

          // Les rouleaux de droite font plus de tours : ils tournent plus longtemps
          // sans paraître plus lents.
          reels.forEach(function (r, i) {
            var cycles = 2 + i;
            var html = '';
            for (var c = 0; c < cycles * 10 + r.target + 1; c++) {
              html += '<span>' + (c % 10) + '</span>';
            }
            // Cellule après la cible : c'est elle qu'on aperçoit pendant le rebond final.
            html += '<span>' + ((r.target + 1) % 10) + '</span>';
            r.strip.innerHTML = html;
            r.stop  = cycles * 10 + r.target;  // index de la cellule d'arrêt
            r.cells = cycles * 10 + r.target + 2;
          });

          var sr = document.createElement('span');
          sr.className = 'kf-sr';
          sr.textContent = raw;

          el.textContent = '';
          el.appendChild(sr);
          el.appendChild(visual);
          gsap.set(el, { opacity: 0, y: 20 });
          restores.push(function () { el.textContent = raw; });

          window.ScrollTrigger.create({
            trigger: el,
            start: 'top 85%',
            once: true,
            onEnter: function () {
              gsap.to(el, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' });
              visual.classList.add('is-spinning');

              var tl = gsap.timeline({
                onComplete: function () { el.textContent = raw; }
              });
              reels.forEach(function (r, i) {
                var duration = 1.4 + i * 0.25;
                tl.fromTo(r.strip,
                  { yPercent: 0 },
                  {
                    yPercent: -100 * r.stop / r.cells,
                    duration: duration,
                    ease: 'back.out(1.1)'
                  }, 0);
                // Flou de vitesse : fort au départ, il retombe avec la vitesse du rouleau.
                tl.fromTo(r.strip,
                  { filter: 'blur(3px)' },
                  { filter: 'blur(0px)', duration: duration * 0.7, ease: 'power1.out' }, 0);
              });
            }
          });
        });
      }

      // G · Chiffres-clés en bento — les tuiles apparaissent en cascade à l'entrée
      // dans le viewport (montée + léger zoom arrière) ; l'image se dézoome dans
      // son cadre. Les chiffres, eux, ne sont pas animés.
      if (hasScrollTrigger) {
        gsap.utils.toArray('.key-figures--bento .key-figures__grid').forEach(function (grid) {
          var tiles = grid.querySelectorAll('.key-figures__item, .key-figures__media');
          if (!tiles.length) { return; }
          var img = grid.querySelector('.key-figures__media img');

          gsap.set(tiles, { opacity: 0, y: 48, scale: 0.96 });
          if (img) { gsap.set(img, { scale: 1.15 }); }

          window.ScrollTrigger.create({
            trigger: grid,
            start: 'top 85%',
            once: true,
            onEnter: function () {
              gsap.to(tiles, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: 'power3.out',
                stagger: 0.12,
                // Retire le transform une fois joué (ne gêne pas d'éventuels :hover).
                onComplete: function () { gsap.set(tiles, { clearProps: 'transform' }); }
              });
              if (img) {
                gsap.to(img, { scale: 1, duration: 1.4, ease: 'power2.out' });
              }
            }
          });
        });
      }

      // H · En-tête single — séquence coordonnée (enseigne → titre → chips).
      // Le titre est découpé en lignes (SplitText) DANS ce même timeline pour
      // éviter tout décalage : tout est gaté sur document.fonts.ready et joué en
      // une seule chaîne, plutôt que de laisser le titre sur un ScrollTrigger séparé.
      var singleHead = document.querySelector('.single-offre__head');
      if (singleHead) {
        var enseigne    = singleHead.querySelector('.single-offre__enseigne');
        var singleTitle = singleHead.querySelector('.single-offre__title');
        var chips       = gsap.utils.toArray('.single-offre__details li', singleHead);

        document.fonts.ready.then(function () {
          var singleTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

          if (enseigne) {
            singleTl.from(enseigne, { opacity: 0, y: 20, duration: 0.5 });
          }

          if (singleTitle && hasSplitText) {
            var titleSplit = window.SplitText.create(singleTitle, {
              type: 'lines',
              mask: 'lines',
              linesClass: 'anim-line'
            });
            singleTl.from(titleSplit.lines, {
              yPercent: 110,
              duration: 0.6,
              stagger: 0.12,
              // Rend le titre au DOM après le reveal (retire le masque → pas de clip).
              onComplete: function () { titleSplit.revert(); }
            }, enseigne ? '-=0.25' : 0);
          } else if (singleTitle) {
            singleTl.from(singleTitle, { opacity: 0, y: 24, duration: 0.6 }, enseigne ? '-=0.25' : 0);
          }

          if (chips.length) {
            singleTl.from(chips, {
              opacity: 0,
              y: 16,
              duration: 0.45,
              stagger: 0.07
            }, '-=0.2');
          }
        });
      }

      // Annulation du contexte : rendre aux chiffres-clés leur valeur d'origine.
      return function () {
        restores.forEach(function (restore) { restore(); });
      };

    });
  });
})();
